import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Menu, X } from 'lucide-react';

const Home = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-cream">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-soft-light backdrop-blur-sm border-b border-taupe/20">
        <div className="container mx-auto px-4 sm:px-6 py-2 flex justify-between items-center">
          <div className="logo-container">
            <img
              src="https://customer-assets.emergentagent.com/job_midlife-mentor/artifacts/h49dm4c7_The%20Radiant%20Phoenix%20Logo-2.png"
              alt="The Radiant Phoenix"
              className="h-20 sm:h-24 md:h-32 w-auto" />
          </div>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            <a href="#about" className="nav-link">About</a>
            <a href="/work-with-me" className="nav-link">Work With Me</a>
            <a href="/get-in-touch" className="nav-link">Get in Touch</a>
            <a href="https://lindairuohaofficial.substack.com/subscribe" target="_blank" rel="noopener noreferrer" className="nav-link">Subscribe</a>
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 text-gray-900 hover:text-gold transition-colors"
            aria-label="Toggle menu">
            {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <nav className="md:hidden bg-soft-light border-t border-taupe/20">
            <div className="container mx-auto px-4 py-4 flex flex-col space-y-4">
              <a
                href="#about"
                className="nav-link text-lg py-2"
                onClick={() => setMobileMenuOpen(false)}>
                About
              </a>
              <a
                href="/work-with-me"
                className="nav-link text-lg py-2"
                onClick={() => setMobileMenuOpen(false)}>
                Work With Me
              </a>
              <a
                href="/get-in-touch"
                className="nav-link text-lg py-2"
                onClick={() => setMobileMenuOpen(false)}>
                Get in Touch
              </a>
              <a
                href="https://lindairuohaofficial.substack.com/subscribe"
                target="_blank"
                rel="noopener noreferrer"
                className="nav-link text-lg py-2"
                onClick={() => setMobileMenuOpen(false)}>
                Subscribe
              </a>
            </div>
          </nav>
        )}
      </header>

      {/* Hero Section */}
      <section className="pt-28 sm:pt-36 md:pt-52 pb-12 sm:pb-16 md:pb-20 px-4 sm:px-6">
        <div className="container mx-auto max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 md:gap-16 items-center">
            {/* Headline and Tagline */}
            <div className="space-y-4 sm:space-y-6">
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-light font-display leading-tight text-gray-900 italic">
                Mentorship for women who juggle career, life and personal growth who are navigating midlife transitions
              </h2>
              <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-display-italic text-taupe-dark">
                Reconnect with your true self - your spirit - and reclaim your energy, creativity and confidence without societal and scientific dogma.
              </p>
              <div className="pt-4 sm:pt-6">
                <Button
                  size="lg"
                  className="w-full sm:w-auto border-4 border-[#C9A84C] bg-transparent text-[#0D1B2A] hover:bg-[#C9A84C] hover:text-white px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg font-semibold transition-all"
                  onClick={() => window.open('https://lindairuohaofficial.substack.com/subscribe', '_blank')}>
                  Join the conversation here
                </Button>
              </div>
            </div>

            {/* Hero Image */}
            <div className="relative order-first lg:order-last">
              <div className="hero-image-wrapper">
                <img
                  src="https://customer-assets.emergentagent.com/job_midlife-mentor/artifacts/hxtuas40_brandagency-render-32.png"
                  alt="Linda Iruoha - The Radiant Phoenix"
                  className="w-full h-auto rounded-lg shadow-xl" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 bg-soft-light">
        <div className="container mx-auto max-w-4xl">
          <div className="space-y-4 sm:space-y-6 text-base sm:text-lg md:text-xl font-body text-gray-700 leading-relaxed">
            <p>Let's be honest — midlife can be brutal.</p>
            <p>
              The burnout, the brain fog, the exhaustion that sleep does not fix. The creeping sense that you've lost touch with yourself somewhere along the way. And the quiet, unsettling question underneath it all: <span className="italic">Is this really it?</span>
            </p>
            <p>
              If this season of life has left you thinking: <span className="font-semibold">"I don't know who I am anymore"</span> OR <span className="font-semibold">"I can't keep pretending I'm fine. Something is seriously broken, and I have to fix it before it destroys everything I've built"</span>, The Radiant Phoenix is for you.
            </p>
            <p>
              Transform your life and health in a way that truly aligns with the natural laws and your spirit's purpose, and go from the debilitating quest for success to life-giving and healing fulfilment of the call of your spirit.
            </p>
            <p className="font-semibold">No masks. No quick fixes. No performance. Just radical honesty with yourself and a bold resolve to step into the new.</p>

            <div className="pt-6 sm:pt-8 text-center">
              <Button
                size="lg"
                className="w-full sm:w-auto border-4 border-[#C9A84C] bg-transparent text-[#0D1B2A] hover:bg-[#C9A84C] hover:text-white px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg font-semibold transition-all"
                onClick={() => window.open('https://lindairuohaofficial.substack.com/subscribe', '_blank')}>
                Subscribe and join the conversation
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Life Approach Section */}
      <section className="py-12 sm:py-16 md:py-20 px-4 sm:px-6">
        <div className="container mx-auto max-w-4xl text-center space-y-6 sm:space-y-8">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-light font-display text-gray-900 italic">
            A True Life Approach to Reclaiming Your Energy, Emotional Balance and Confidence
          </h2>
          <p className="text-base sm:text-lg md:text-xl font-body text-gray-700 leading-relaxed">
            Attitude is everything and you hold the key to a fulfilled life, full of joy, creativity and energy.
          </p>
          <p className="text-base sm:text-lg md:text-xl font-body text-gray-700 leading-relaxed">
            That is the truth most of us can no longer understand, because we have forgotten who we truly are, why we are here, and how to live.
          </p>
          <p className="text-base sm:text-lg md:text-xl font-body text-gray-700 leading-relaxed">
            You have spent years building — your career, your relationships, your reputation, your life. And yet here you are, exhausted in ways sleep cannot fix, searching for words that used to come easily, wondering in quiet moments whether the woman you were is somehow slipping away.
          </p>
        </div>
      </section>

      {/* Wobbly Bridge Section */}
      <section className="py-12 sm:py-16 md:py-20 px-4 sm:px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-10 md:gap-12 items-center">
            <div className="space-y-6 sm:space-y-8">
              <h3 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-display-italic text-gray-900 italic">This Season of Life Is Like Stepping Onto a Wobbly Bridge…
              </h3>
              <div className="space-y-4 sm:space-y-6 text-base sm:text-lg md:text-xl font-body text-gray-700 leading-relaxed">
                <p>
                  This season need not be a crisis — it could become for you the crossing that it really should be. From gathering life experiences to now weaving consciously - taking every experience, every scar, every hard-won piece of wisdom, and begin to live according to your spirit's purpose.
                </p>
                <p>
                  The fatigue, the fog, the restlessness, the strange grief for a self you cannot quite name — these are not your enemies. They are messengers, asking you to look honestly at the foundations beneath your life and discern which ones were truly built by your own spirit, and which were laid by fear, by expectation, by someone else's vision of who you should be.
                </p>
                <p>
                  Your energy, your clarity, your confidence simply need to be built up anew on a more solid ground of trust...<br />
                  <br />
                  ...trust in the Creator...<br />
                  <br />
                  ...trust in the natural laws of life...<br />
                  <br />
                  ...trust in the voice of your spirit.
                </p>
                <p className="text-base sm:text-lg md:text-xl font-semibold italic text-gray-900">I know this bridge only too well...</p>
              </div>
            </div>

            <div className="order-first lg:order-last">
              <img
                src="https://customer-assets.emergentagent.com/job_midlife-mentor/artifacts/7f0hklb7_brandagency-render-31.png"
                alt="Woman in golden dress walking on a wooden pier - stepping onto the bridge of midlife transition"
                className="w-full h-auto rounded-lg shadow-lg" />

            </div>
          </div>
        </div>
      </section>

      {/* About Linda Section */}
      <section id="about" className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 bg-soft-light">
        <div className="container mx-auto max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-10 md:gap-12 items-center">
            <div className="order-2 lg:order-1">
              <img src="https://customer-assets.emergentagent.com/job_midlife-mentor/artifacts/3mx74s1f_brandagency-render-33.png" alt="Linda Iruoha" className="w-full h-auto rounded-lg shadow-lg" />
            </div>
            <div className="order-1 lg:order-2 space-y-4 sm:space-y-6">
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-light font-display text-gray-900 italic">Hello, I'm Linda Iruoha…

              </h2>
              <div className="space-y-3 sm:space-y-4 text-base sm:text-lg md:text-xl font-body text-gray-700 leading-relaxed">
                <p>
                  8 years ago, my midlife transition began. Unable to keep up with the fast-paced beat of London city life as a high-achieving senior business consultant due to increasingly debilitating fatigue, brain fog and emotional outbursts, I found myself at a crossroads - continue pushing through and pursuing my life and career goals as I have done so far, or take a pause and listen to what life was trying to tell me.
                </p>
                <p className="italic">I chose the latter.</p>
                <p>Today, I help women who juggle career, life and personal growth—whether corporate leaders or entrepreneurs—who have found themselves in this season of life and struggling with fatigue, brain fog, and disconnected from themselves, reconnect with their spirit and inner guidance, so that the transformation comes from both the inside out and the outside in.

                </p>
                <p>

                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* You Hold The Key Section */}
      <section className="py-12 sm:py-16 md:py-20 px-4 sm:px-6">
        <div className="container mx-auto max-w-4xl space-y-6 sm:space-y-8">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-light font-display text-gray-900 text-center italic">
            You Hold The Key To Your Healing
          </h2>
          <div className="space-y-4 sm:space-y-6 text-base sm:text-lg md:text-xl font-body text-gray-700 leading-relaxed text-center">
            <p>
              You have tried the supplements, the sleep routines, the productivity hacks, the wellness programmes.
            </p>
            <p className="text-center italic">Some helped. For a while.</p>
            <p className="text-center italic"></p>
            <p>
              But nothing has fully stuck, because none of it starts where healing actually begins: with you - your spirit, who you truly are.
            </p>
            <p>
              Contrary to conventional wisdom, true healing is not a formula. It is not a protocol someone hands you. It is a path that can only be walked from the inside out…
            </p>
            <p className="text-center italic">… and only you hold the map.</p>
            <p>
              When you reconnect with your true self, the path that is uniquely yours reveals itself. The fog lifts. The exhaustion finds its meaning. The confusion gives way to clarity.
            </p>
            <p>
              And the confidence you have lost grows unshakably on a new, more solid foundation.
            </p>
            <p className="font-semibold">
              This is not about doing more. It is about returning to the one source that has never failed you — your spirit.
            </p>
            <p className="text-center italic">That is where we begin.</p>
          </div>
        </div>
      </section>

      {/* Spirit Calling Section */}
      <section id="approach" className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 bg-soft-light">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-light font-display text-gray-900 text-center mb-8 sm:mb-10 md:mb-12 italic">
            Beneath the Noise, Your Spirit Never Stopped Calling.
          </h2>
          
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-10 md:gap-12 items-center">
            <div className="space-y-4 sm:space-y-6 text-base sm:text-lg md:text-xl font-body text-gray-700 leading-relaxed">
              <p>
                In the quiet beneath the exhaustion, beneath the fog, beneath the fear, there is a voice. Steady. Knowing. Unhurried. It has never left you. It has simply been waiting for you to stop long enough to listen.
              </p>
              <p className="!font-semibold !italic">
                The Radiant Phoenix is where that conversation begins.
              </p>
              <p>
                A space created for women in midlife who are ready to stop managing their depletion and start living from their depth. Every issue is an invitation to reflect, to realign, and to hear the voice that knows exactly who you are and where you are going.
              </p>
              <p className="font-semibold">No noise. Just truth. Truth that stirs something in you that you recognise as real.</p>
              <p className="text-base sm:text-lg md:text-xl italic text-gray-900 pt-3 sm:pt-4">
                Join the conversation and Make Room For Your Spirit to Take Flight.
              </p>
            </div>
            
            <div className="order-first lg:order-last">
              <img src="https://customer-assets.emergentagent.com/job_midlife-mentor/artifacts/roxhhyek_brandagency-render-34.png"
              alt="Linda Iruoha - The Radiant Phoenix"
              className="w-full h-auto rounded-lg shadow-lg" />
            </div>
          </div>
          
          <div className="flex justify-center pt-8 sm:pt-10 md:pt-12">
            <Button
              size="lg"
              className="w-full sm:w-auto border-4 border-[#C9A84C] bg-transparent text-[#0D1B2A] hover:bg-[#C9A84C] hover:text-white px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg font-semibold transition-all"
              onClick={() => window.open('https://lindairuohaofficial.substack.com/subscribe', '_blank')}>

              Let My Spirit Lead
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 sm:py-10 md:py-12 px-4 sm:px-6 bg-white border-t border-taupe/20">
        <div className="container mx-auto max-w-7xl">
          <div className="flex flex-col items-center gap-3 sm:gap-4">
            <div>
              <img
                src="https://customer-assets.emergentagent.com/job_midlife-mentor/artifacts/h49dm4c7_The%20Radiant%20Phoenix%20Logo-2.png"
                alt="The Radiant Phoenix"
                className="h-24 sm:h-32 md:h-40 w-auto" />
            </div>
            <div className="text-center">
              <p className="text-sm sm:text-base font-body text-gray-600">© 2026 The Radiant Phoenix. All rights reserved.

              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>);

};

export default Home;